import {
  AttemptsByDayChart,
  AttemptsRankingChart,
  ChartGrid,
  FinancialChart,
  PromisesChart,
  ResultsChart,
} from 'components/Billing/Charts';
import PeriodFilter from 'components/Billing/PeriodFilter';
import SummaryCards from 'components/Billing/SummaryCards';
import BasicTable from 'components/Form/Table';
import { Select } from 'components/Form/Select';
import TableWrapper from 'components/TableWrapper';
import ContainerAuthenticated from 'containers/Authenticated';
import useOperatorPerformance from 'hooks/useOperatorPerformance';
import { useMemo } from 'react';
import { PERFORMANCE_EXPORT_COLUMNS } from 'utils/billingReports';
import { downloadSpreadsheetCSV } from 'utils/downloads';
import { parseCurrency } from 'utils/parsers';
import { OperatorField, PanelPage } from './styled';

// Valor do seletor para "todos": o Select compartilhado devolve o `id` como
// string, e um `id` vazio nao seleciona nada.
const ALL_OPERATORS = 'all';

const rate = (value) =>
  Number.isFinite(value) ? `${`${value}`.replace('.', ',')}%` : '';

const money =
  (field) =>
  ({ row }) =>
    row?.[field] ? parseCurrency(row[field]) : '';

const COLUMNS = [
  { title: 'Operador', ref: 'operator', sortable: true },
  { title: 'Tentativas', ref: 'attempts', sortable: true },
  { title: 'Contatos', ref: 'effective', sortable: true },
  {
    title: '% Contato',
    ref: 'effectiveRate',
    sortable: true,
    renderCell: ({ row }) => rate(row?.effectiveRate),
  },
  { title: 'Acordos', ref: 'agreements', sortable: true },
  {
    title: 'Promessas',
    ref: 'promises',
    sortable: true,
    renderCell: ({ row }) =>
      !row?.promises
        ? ''
        : `${row.promisesKept} cumpridas · ${row.promisesBroken} quebradas${
            row.promisesPending ? ` · ${row.promisesPending} no prazo` : ''
          }`,
  },
  {
    title: '% Cumpridas',
    ref: 'promiseRate',
    sortable: true,
    renderCell: ({ row }) => rate(row?.promiseRate),
  },
  { title: 'Renegociações', ref: 'renegotiations', sortable: true },
  {
    title: 'Renegociado',
    ref: 'renegotiatedVolume',
    sortable: true,
    renderCell: money('renegotiatedVolume'),
  },
  { title: 'Canceladas', ref: 'cancelledRenegotiations', sortable: true },
  { title: 'Baixas', ref: 'settlements', sortable: true },
  {
    title: 'Baixado',
    ref: 'settledVolume',
    sortable: true,
    renderCell: money('settledVolume'),
  },
  { title: 'Encaminhamentos', ref: 'forwards', sortable: true },
];

// Painel de desempenho por operador do menu Cobranca: atendimento, promessas
// e resultado financeiro no periodo. Os numeros vem do
// useOperatorPerformance; os graficos, de components/Billing/Charts. Sem a
// flag `desempenho_cobranca` o painel mostra so o proprio usuario e nao tem
// o seletor de operador.
export default function BillingRelatorioDesempenho() {
  const {
    loading,
    period,
    setPeriod,
    validRange,
    seeAll,
    operators,
    selectedOperator,
    setSelectedOperator,
    rows,
    daily,
    results,
    cards,
  } = useOperatorPerformance();

  const buttons = useMemo(
    () => [
      {
        label: 'Exportar',
        icon: 'download',
        color: 'blue',
        border: true,
        action: () =>
          downloadSpreadsheetCSV(
            PERFORMANCE_EXPORT_COLUMNS,
            rows,
            `desempenho_${period.from}_a_${period.to}.csv`
          ),
      },
    ],
    [rows, period]
  );

  const operatorOptions = useMemo(
    () => [
      { id: ALL_OPERATORS, title: 'Todos os operadores' },
      ...operators.map((item) => ({ id: item.value, title: item.label })),
    ],
    [operators]
  );

  return (
    <ContainerAuthenticated>
      <PanelPage>
        <TableWrapper
          title="Desempenho"
          subTitle={
            seeAll
              ? 'Atendimento, promessas e resultado por operador'
              : 'Seus números de atendimento, promessas e resultado'
          }
          buttons={buttons}
          filters={
            <PeriodFilter
              period={period}
              onChangePeriod={setPeriod}
              invalid={!validRange}
            >
              {!seeAll ? null : (
                <OperatorField>
                  <Select
                    small
                    options={operatorOptions}
                    value={selectedOperator || ALL_OPERATORS}
                    onChange={(value) =>
                      setSelectedOperator(value === ALL_OPERATORS ? '' : value)
                    }
                  />
                </OperatorField>
              )}
            </PeriodFilter>
          }
        >
          <SummaryCards cards={cards} />
          <ChartGrid>
            <AttemptsByDayChart daily={daily} />
            <ResultsChart results={results} />
            <PromisesChart rows={rows} />
            <AttemptsRankingChart rows={rows} />
            <FinancialChart rows={rows} />
          </ChartGrid>
          <BasicTable loading={loading} columns={COLUMNS} rows={rows} />
        </TableWrapper>
      </PanelPage>
    </ContainerAuthenticated>
  );
}
